import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Space, Button, Modal, Form, InputNumber, Input, Select, message } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { ResourceList } from '@/components/ResourceList';
import { listUsers, type AdminUser } from '@/api/users';
import { grant } from '@/api/diamonds';
import { listApps, type App } from '@/api/apps';
import { formatCNY, formatDateTime, truncate } from '@/utils/format';

export function UsersListPage() {
  // Remount key — bump after a successful grant so the list (and the granted
  // user's balance) refetches. Same pattern as the coupons page.
  const [reloadKey, setReloadKey] = useState(0);
  // Grant modal targets one user at a time.
  const [granting, setGranting] = useState<AdminUser | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // App selector options — the grant endpoint requires an app_id (钻石按 App 记账).
  const [apps, setApps] = useState<App[]>([]);
  useEffect(() => {
    listApps({ page_size: 100 })
      .then((res) => setApps(res.data))
      .catch(() => {
        /* 非阻塞: 拉不到 App 列表时仍可手填 App ID */
      });
  }, []);

  const openGrant = (user: AdminUser) => {
    setGranting(user);
    form.resetFields();
  };

  const handleGrant = async () => {
    if (!granting) return;
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      await grant(granting.id, {
        app_id: v.app_id,
        amount: v.amount,
        reason: 'admin_grant',
        notes: v.notes || undefined,
        // 后端要求幂等键 (header 或 body), 每次提交生成一个新的.
        idempotency_key:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `grant-${granting.id}-${Date.now()}`,
      });
      message.success('已加钻石');
      setGranting(null);
      form.resetFields();
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ResourceList<AdminUser>
        key={reloadKey}
        title="用户"
        description="跨 App 视角；platforms 字段聚合 user_app_relations 内的所有平台。"
        rowKey="id"
        pagination="offset"
        filters={[
          { key: 'q', label: '关键字', type: 'text', placeholder: '昵称 / 手机 / id' },
          { key: 'app_id', label: 'App ID', type: 'text', placeholder: '数字' },
          { key: 'min_ltv_cents', label: '最低 LTV (分)', type: 'text', placeholder: '如 1000' },
        ]}
        fetcher={({ filters, page, pageSize }) => {
          const f = { ...filters } as Record<string, unknown>;
          if (typeof f.app_id === 'string') f.app_id = Number(f.app_id);
          if (typeof f.min_ltv_cents === 'string') f.min_ltv_cents = Number(f.min_ltv_cents);
          return listUsers({ ...f, page, page_size: pageSize } as never);
        }}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            render: (v) => <Link to={`/users/${v}`}>{truncate(v, 14)}</Link>,
          },
          { title: '昵称', dataIndex: 'nickname', render: (v) => v || '-' },
          { title: '手机', dataIndex: 'phone_masked', render: (v) => v || '-' },
          {
            title: '平台',
            dataIndex: 'platforms',
            render: (v: string[] | undefined) => (
              <Space size={4}>
                {(v ?? []).map((p) => (
                  <Tag key={p}>{p}</Tag>
                ))}
              </Space>
            ),
          },
          {
            title: 'LTV',
            dataIndex: 'lifetime_value_cents',
            align: 'right',
            render: (v) => formatCNY(v),
          },
          {
            title: '钻石',
            dataIndex: 'diamond_balance',
            align: 'right',
            render: (v) => v ?? 0,
          },
          {
            title: '注册时间',
            dataIndex: 'created_at',
            render: (v) => formatDateTime(v),
          },
          {
            title: '操作',
            key: 'actions',
            render: (_, row) => (
              <Button
                type="link"
                size="small"
                icon={<PlusCircleOutlined />}
                onClick={() => openGrant(row)}
              >
                加钻石
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title={granting ? `加钻石 · ${granting.nickname || truncate(granting.id, 14)}` : '加钻石'}
        open={granting !== null}
        okText="确认"
        confirmLoading={submitting}
        onOk={handleGrant}
        onCancel={() => setGranting(null)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="app_id"
            label="归属 App"
            rules={[{ required: true, message: '请选择归属 App' }]}
            tooltip="钻石按 App 记账；选择本次发放归属的 App"
          >
            <Select
              showSearch
              placeholder="选择 App"
              optionFilterProp="label"
              options={apps.map((a) => ({ value: a.id, label: `${a.name} (#${a.id})` }))}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="数量"
            rules={[
              { required: true, message: '请输入数量' },
              { type: 'number', min: 1, message: '需为正整数' },
            ]}
          >
            <InputNumber min={1} max={100000} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
